import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAccount } from 'wagmi'
import { T } from '@/lib/theme'
import { SectionHeader } from '@/components/dashboard/SectionHeader'
import { Button, Modal, Panel, SectionLabel, Skeleton, Stat, clickable } from '@/components/ui'
import { fmt, fmtUsd, fmtPct, fmtBps } from '@/lib/utils'
import { useProtocolState } from '@/hooks/useProtocolState'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'
import { coldStartSupplyAprBps } from '@/lib/coldStart'
import { TokenGlyph } from '@/components/dashboard/TokenGlyph'
import { ProtectionPanel } from '@/components/dashboard/ProtectionPanel'
import { EmptyState } from '@/components/dashboard/EmptyState'
import {
  SupplyForm, BorrowForm, FacilitatorSupplyNote, IsolationWarnings, ZONE_META,
} from '@/components/dashboard/markets/ReserveForms'

/// Borrow/Supply side of the money market: one Aave-like symmetric pool, not two separate
/// markets - any reserve (native ETH, USDx) can be supplied as collateral AND borrowed against,
/// with one health factor spanning the whole account across every reserve. This replaced the
/// earlier two-market design (`HorizonMoneyMarket` "Market A": ETH collateral only, USDx debt only;
/// `HorizonMoneyMarketEth` "Market B": sUSDx collateral only, ETH debt only) - see
/// HorizonLendingPool.sol's own doc comment for why.
///
/// **Deliberately flat.** The reference this page's layout follows (Kamino's Borrow surface) groups
/// assets under collapsible market headers, because it runs 32 separate markets each holding many
/// assets. Horizon is ONE pool, so that hierarchy would be a parent row the contracts do not have -
/// see UI_PLAN.md for the full list of reference patterns taken and refused.
///
/// A row now navigates to `/markets/:asset` for the full picture, and carries its own Supply and
/// Borrow buttons for the fast path. The action forms themselves live in
/// `markets/ReserveForms.jsx`, shared verbatim with the detail page.
export function MarketsSection() {
  const { address: user } = useAccount()
  const { d, deployed, isLoading, refetch } = useProtocolState()
  const hasReserves = d.reserves.length > 0
  // `null`, or `{ reserveKey, action }` - the quick-action modal opened from a row.
  const [action, setAction] = useState(null)

  const actionReserve = action ? d.reserves.find((r) => r.key === action.reserveKey) : null

  const marketSizeUsd = d.reserves.reduce((s, r) => s + r.totalSupplied * r.priceUsd, 0)
  const activeBorrowsUsd = d.reserves.reduce((s, r) => s + r.totalBorrows * r.priceUsd, 0)

  return (
    <div id="markets">
      <div style={headerRow}>
        <SectionHeader
          title="Markets"
          subtitle="Supply any asset to earn yield, use it as collateral, and borrow against it."
        />
        {deployed && hasReserves && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <SummaryPill label="Market Size" value={fmtUsd(marketSizeUsd)} />
            <SummaryPill label="Active Borrows" value={fmtUsd(activeBorrowsUsd)} />
          </div>
        )}
      </div>

      {!deployed ? (
        // Not deployed on the connected network (pre-launch, or a wrong-network wallet): show one
        // calm not-live panel instead of a health factor and reserve rows full of $0.00 / 0.00% that
        // read as a half-built market. Same gate the Vaults page uses. Reserves, rates, and balances
        // appear here the moment the money market is live on this network.
        <EmptyState
          title="The money market is not live on this network yet."
          body="Point your wallet at a network where Horizon's lending pool is deployed. Reserves, rates, and your balances appear here as soon as it is." />
      ) : (isLoading && !hasReserves) ? (
        // First load: show skeletons rather than falling through to a $0.00 health panel and an
        // empty table, which reads as a half-built market for the beat before reads resolve.
        <MarketsLoading />
      ) : (!hasReserves) ? (
        // Deployed, but no reserves listed yet (staged launch: the pool ships empty and each asset
        // market is added post-launch). Show a ready state, not an empty health factor + table.
        <EmptyState
          title="No markets listed yet."
          body="The money market is live. Each asset market appears here as soon as it is listed." />
      ) : (
        <>
          <HealthPanel d={d} />

          {/* Directly under the health factor, because it is the one action that changes what that
              number does next. Renders itself away for an account with no debt - protection on a
              position that cannot be liquidated would be a control with nothing to control. */}
          <ProtectionPanel d={d} onDone={refetch} />

          <div style={{ margin: '24px 0' }}>
            <MarketsTable reserves={d.reserves} onAction={setAction} />
          </div>

          <div style={{ textAlign: 'center' }}>
            <Link to="/portfolio" style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, textDecoration: 'none' }}>
              Every market here earns HORIZON on top - view &amp; claim it on Portfolio →
            </Link>
          </div>
        </>
      )}

      <Modal open={!!actionReserve} onClose={() => setAction(null)} width={480}>
        {actionReserve && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <IsolationWarnings reserve={actionReserve} d={d} user={user} />
            {action.action === 'supply'
              ? (actionReserve.isFacilitator
                  ? <FacilitatorSupplyNote reserve={actionReserve} />
                  : <SupplyForm key={`${actionReserve.key}-supply`} reserve={actionReserve} d={d} user={user} onTx={refetch} />)
              : <BorrowForm key={`${actionReserve.key}-borrow`} reserve={actionReserve} d={d} user={user} onTx={refetch} />}
            <Link to={`/markets/${actionReserve.asset}`} onClick={() => setAction(null)} style={detailLink}>
              Full {actionReserve.symbol} market details →
            </Link>
          </div>
        )}
      </Modal>
    </div>
  )
}

function SummaryPill({ label, value }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'baseline', gap: 10,
      padding: '10px 16px', borderRadius: T.r.pill,
      border: `1px solid ${T.border}`, background: T.bg2,
    }}>
      <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>{label}</span>
      <span className="mono" style={{ color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, fontWeight: 700 }}>{value}</span>
    </div>
  )
}

// First-load skeleton: mirrors the real layout (a health panel block + a few table rows) so the
// page holds its shape while the multicall reads resolve, instead of flashing zeros.
function MarketsLoading() {
  return (
    <>
      <Panel style={{ padding: 32 }}>
        <Skeleton width={130} height={13} />
        <div style={{ marginTop: 16 }}><Skeleton width="100%" height={64} /></div>
        <div style={{ marginTop: 20 }}><Skeleton width="100%" height={10} /></div>
      </Panel>
      <div style={{ margin: '24px 0' }}>
        <Panel style={{ padding: 20 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: i === 2 ? 'none' : `1px solid ${T.border}` }}>
              <Skeleton width={150} height={20} />
              <Skeleton width={70} height={16} />
              <Skeleton width={70} height={16} />
              <Skeleton width={54} height={54} />
              <Skeleton width={96} height={32} />
            </div>
          ))}
        </Panel>
      </div>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────
// Markets table - one row per reserve. The row itself navigates to the reserve's detail page;
// the two buttons open the quick-action modal without leaving the list. That split matters:
// comparing reserves and acting on one are different tasks, and making the whole row a single
// ambiguous target served neither.
// ─────────────────────────────────────────────────────────────────

function MarketsTable({ reserves, onAction }) {
  const navigate = useNavigate()
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()
  // getReservesList() is registration order (see useProtocolState DISPLAY_ORDER), so the last reserve
  // is the most-recently listed - badge it "New" so a freshly added market gets a signal in the list.
  // At genesis the founding reserves are legitimately new too, so no time-gate is needed.
  const newestKey = reserves.length ? reserves[reserves.length - 1].key : null

  return (
    <Panel style={{ padding: 0 }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>Asset</th>
              <th style={{ ...th, textAlign: 'right' }}>Total Supplied</th>
              <th style={{ ...th, textAlign: 'right' }}>Supply APY</th>
              <th style={{ ...th, textAlign: 'right' }}>Total Borrowed</th>
              <th style={{ ...th, textAlign: 'right' }}>Borrow APY</th>
              <th style={{ ...th, textAlign: 'center' }}>Utilization</th>
              {/* The liquidation threshold, labelled to match the reserve detail page (which calls
                  this same value "Liquidation threshold"), not the ambiguous "Liq LTV". */}
              <th style={{ ...th, textAlign: 'right' }}>Liq. threshold</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {reserves.map((r) => {
              return (
                <tr
                  key={r.key}
                  {...clickable(() => navigate(`/markets/${r.asset}`), { label: `Open ${r.symbol} market` })}
                  style={marketRow}
                >
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <TokenGlyph symbol={r.symbol} size={40} />
                      <span style={{ color: T.text, fontFamily: T.fontM, fontSize: 19, fontWeight: 700 }}>{r.symbol}</span>
                      {r.key === newestKey && <span style={newPill}>New</span>}
                      {/* Isolated reserves behave differently enough that it has to be visible in
                          the list, not discovered on submit - see IsolationWarnings. */}
                      {r.isIsolated && <span style={isolatedPill}>Isolated</span>}
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {/* USD is the primary figure - it's the value that's comparable across a mixed
                        ETH/USDC/wstETH table; the token amount is the sub-line. */}
                    <div className="mono" style={cellPrimary}>{fmtUsd(r.totalSupplied * r.priceUsd)}</div>
                    <div style={cellSub}>{fmt(r.totalSupplied, 2)} {r.symbol}</div>
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {(() => {
                      // Cold-start estimate for an empty market: the effective rate the first
                      // line would show is zero BY the APR governor's own min() on a zero-TVL
                      // farm, while a real budgeted stream waits for the first depositor. Same
                      // shared estimate Earn's pool cards use; the assumption sits in the sub.
                      const estBps = coldStartSupplyAprBps(r, horizonUsd, hasHorizonPrice)
                      return estBps > 0 ? (
                        <>
                          <span className="mono" style={{ ...cellPrimary, color: T.positive }}>≈ {fmtBps(estBps)}</span>
                          <div style={cellSub}>est. HORIZON for the first $10k supplied</div>
                        </>
                      ) : (
                        <>
                          <span className="mono" style={{ ...cellPrimary, color: T.positive }}>{fmtBps(r.totalSupplyAprBps)}</span>
                          {r.supplyHorizonAprBps > 0 && <div style={cellSub}>incl. {fmtBps(r.supplyHorizonAprBps)} HORIZON{r.supplyHorizonCapped ? ' · capped' : ''}</div>}
                        </>
                      )
                    })()}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <div className="mono" style={cellPrimary}>{fmtUsd(r.totalBorrows * r.priceUsd)}</div>
                    <div style={cellSub}>{fmt(r.totalBorrows, 2)} {r.symbol}</div>
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <span className="mono" style={{ ...cellPrimary, color: r.totalBorrowAprBps < 0 ? T.positive : T.text }}>{fmtBps(r.totalBorrowAprBps)}</span>
                    {r.borrowHorizonAprBps > 0 && <div style={cellSub}>net of {fmtBps(r.borrowHorizonAprBps)} HORIZON{r.borrowHorizonCapped ? ' · capped' : ''}</div>}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <UtilizationRing r={r} />
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <span className="mono" style={cellPrimary}>{fmtBps(r.liquidationThresholdBps)}</span>
                  </td>
                  <td style={{ ...td, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 6, alignItems: 'stretch', minWidth: 96 }}>
                      {!r.isFacilitator && (
                        <Button variant="secondary" style={rowBtn} onClick={() => onAction({ reserveKey: r.key, action: 'supply' })}>
                          Supply
                        </Button>
                      )}
                      {/* A collateral-only reserve has no Borrow action at all. Rendering one that
                          reverts BorrowingDisabled on submit is the failure entry 109 fixed on
                          /liquidations; the fix is to not offer it, not to explain it afterwards. */}
                      {!r.borrowingDisabled && (
                        <Button variant="secondary" style={rowBtn} onClick={() => onAction({ reserveKey: r.key, action: 'borrow' })}>
                          Borrow
                        </Button>
                      )}
                      {r.borrowingDisabled && <span style={collateralOnlyPill}>Collateral only</span>}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

// ─────────────────────────────────────────────────────────────────
// Utilization ring - a standardized silver-moon donut whose arc is the reserve's utilization
// (borrowed / supplied). One consistent gradient for every row rather than a per-row hue, so the
// column reads as one comparable scale, not a rainbow. The colour lives in the ring; the numbers
// live in a hover card that follows the ring via a fixed-position anchor (getBoundingClientRect),
// so the table's `overflow-x: auto` can never clip it. The card's coloured legend dots tie each
// figure - supplied / borrowed / cap - back to the palette.
// ─────────────────────────────────────────────────────────────────

function UtilizationRing({ r }) {
  const [tip, setTip] = useState(null) // { x, y } screen coords of the ring's top-centre, or null
  const util = Math.max(0, Math.min(1, (r.utilizationBps ?? 0) / 10000))
  const pct = util * 100
  const size = 54
  const stroke = 5.5
  const radius = (size - stroke) / 2
  const circ = 2 * Math.PI * radius
  const gid = `utilgrad-${r.key}`

  const anchor = (e) => {
    const box = e.currentTarget.getBoundingClientRect()
    setTip({ x: box.left + box.width / 2, y: box.top })
  }
  // Tap-toggle for touch/keyboard: the breakdown is unreachable by hover on a phone, so a tap
  // opens/closes it too (desktop hover still works as before).
  const toggle = (e) => { if (tip) setTip(null); else anchor(e) }

  return (
    <div
      style={{ display: 'inline-flex', position: 'relative', cursor: 'pointer' }}
      onMouseEnter={anchor}
      onMouseMove={anchor}
      onMouseLeave={() => setTip(null)}
      onClick={toggle}
      role="button" tabIndex={0} aria-label={`Utilization ${pct.toFixed(1)}% - tap for supply, debt and cap`}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(e) } }}
    >
      <svg width={size} height={size} style={{ display: 'block' }}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={T.accentLight} />
            <stop offset="55%" stopColor={T.accentDeep} />
            <stop offset="100%" stopColor={T.purple} />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={`url(#${gid})`} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - util)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ filter: 'drop-shadow(0 0 4px rgba(167,139,250,0.45))', transition: 'stroke-dashoffset 0.4s ease' }}
        />
        <text
          x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="mono"
          style={{ fill: T.text, fontFamily: T.fontN, fontSize: 12, fontWeight: 700 }}
        >
          {pct >= 99.95 ? '100%' : `${pct.toFixed(pct < 10 ? 1 : 1)}%`}
        </text>
      </svg>
      {tip && <UtilizationTooltip r={r} pct={pct} x={tip.x} y={tip.y} />}
    </div>
  )
}

function UtilizationTooltip({ r, pct, x, y }) {
  return (
    <div style={{
      position: 'fixed', left: x, top: y - 12, transform: 'translate(-50%, -100%)',
      zIndex: 1000, pointerEvents: 'none', width: 224,
      padding: '13px 15px', borderRadius: T.r.md, textAlign: 'left',
      border: `1px solid ${T.borderHover}`, background: 'rgba(10, 14, 28, 0.97)',
      backdropFilter: 'blur(1.5px) saturate(1.4)', WebkitBackdropFilter: 'blur(1.5px) saturate(1.4)',
      boxShadow: T.shadowMed,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
        <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Utilization
        </span>
        <span className="mono" style={{ color: T.accentLight, fontFamily: T.fontN, fontSize: 14, fontWeight: 700 }}>
          {pct.toFixed(2)}%
        </span>
      </div>
      <TooltipRow dot={T.accentLight} label="Supplied" amt={`${fmt(r.totalSupplied, 2)} ${r.symbol}`} usd={fmtUsd(r.totalSupplied * r.priceUsd)} />
      <TooltipRow dot={T.purple} label="Borrowed" amt={`${fmt(r.totalBorrows, 2)} ${r.symbol}`} usd={fmtUsd(r.totalBorrows * r.priceUsd)} />
      <TooltipRow
        dot="rgba(255,255,255,0.28)" label="Borrow cap"
        amt={r.borrowCap > 0 ? `${fmt(r.borrowCap, 2)} ${r.symbol}` : 'No cap'}
        usd={r.borrowCap > 0 ? fmtUsd(r.borrowCap * r.priceUsd) : ''}
      />
    </div>
  )
}

function TooltipRow({ dot, label, amt, usd }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flexShrink: 0 }} />
      <span style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, flexShrink: 0 }}>{label}</span>
      <span style={{ flex: 1, borderBottom: `1px dotted ${T.border}`, margin: '0 2px', transform: 'translateY(-2px)' }} />
      <div style={{ textAlign: 'right' }}>
        <div className="mono" style={{ color: T.text, fontFamily: T.fontN, fontSize: T.fs.caption, fontWeight: 700 }}>{amt}</div>
        {usd && <div className="mono" style={{ color: T.textDim, fontFamily: T.fontM, fontSize: 10 }}>{usd}</div>}
      </div>
    </div>
  )
}

// AssetIcon used to live here with its own two-token lookup and its own letter fallback. It was
// deleted rather than extended: it is exactly the kind of second source of truth that drifts, and it
// did - WBTC and wstETH rendered as letter badges on this page while showing real artwork
// everywhere else, because only one of the two resolvers knew about them.
//
// TokenGlyph already holds the full table and a deterministic letter fallback for anything unknown,
// so a reserve listed in future gets a sensible glyph here with no edit at all.

// ─────────────────────────────────────────────────────────────────
// Account-wide health - collateral/debt/available-to-borrow (USD, spans every reserve) and a
// health-factor bar. Replaces the old per-market LTV bar: with a symmetric pool a user can
// supply+borrow across both reserves at once, so risk is one account-wide number now, not one
// per market. Stays on the LIST rather than moving to a reserve detail page for the same reason -
// it is a property of the account, not of any one market.
// ─────────────────────────────────────────────────────────────────

export function HealthPanel({ d }) {
  // Nothing supplied and nothing borrowed: don't render three $0.00 stats and a full green bar
  // (which reads as a half-built widget for a browser). One calm line instead.
  const hasPosition = d.accountTotalCollateralUsd > 0 || d.accountTotalDebtUsd > 0
  if (!hasPosition) {
    return (
      <Panel style={{ padding: 28 }}>
        <SectionLabel>Position Health</SectionLabel>
        <div style={{ marginTop: 10, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>
          No position yet. Supply an asset below to start earning, use it as collateral, and borrow against it.
        </div>
      </Panel>
    )
  }

  const hfNum = d.hasDebt && d.healthFactor != null ? Number(d.healthFactor) / 1e18 : null
  const zone = !d.hasDebt ? 'safe' : hfNum >= 1.3 ? 'safe' : hfNum >= 1.05 ? 'caution' : 'danger'
  const { color, label } = ZONE_META[zone]
  // 2.0x health factor fills the bar - purely a display scale, not an on-chain threshold. So the
  // liquidation point (1.00) always sits at exactly 50% of the track, which is where the marker goes.
  const fillPct = hfNum == null ? 100 : Math.max(4, Math.min(100, (hfNum / 2) * 100))

  return (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>Position Health</SectionLabel>
      <div style={{ marginTop: 12, ...statStrip }}>
        <Stat size="cell" label="Total collateral" value={fmtUsd(d.accountTotalCollateralUsd)} />
        <Stat size="cell" divider label="Total debt" value={fmtUsd(d.accountTotalDebtUsd)} />
        <Stat size="cell" divider label="Available to borrow" value={fmtUsd(d.accountAvailableBorrowsUsd)} />
      </div>
      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <span style={inputLabel}>Health Factor</span>
          {/* Promoted to a prominent figure - it's the most load-bearing number on the page. */}
          <span className="mono" style={{ color, fontSize: 22, fontFamily: T.fontN, fontWeight: 700, lineHeight: 1 }}>
            {hfNum != null ? hfNum.toFixed(2) : '∞'}
            <span style={{ fontSize: 12, color: T.textDim, fontWeight: 600 }}> · {d.hasDebt ? label : 'No debt'}</span>
          </span>
        </div>
        <div style={{ position: 'relative', height: 10, borderRadius: T.r.pill, background: 'rgba(255,255,255,0.06)' }}>
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0, width: `${fillPct}%`,
            borderRadius: T.r.pill, background: color, transition: 'width 0.3s ease, background 0.3s ease',
          }} />
          {/* Liquidation marker at 1.00 (always the 50% mark on this HF/2 scale) - the one threshold
              a borrower must never cross, now on the bar itself rather than only in a side caption. */}
          <div style={{ position: 'absolute', left: '50%', top: -4, bottom: -4, width: 2, transform: 'translateX(-50%)', background: T.negative, opacity: 0.85, borderRadius: 1 }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
          <span className="mono" style={{ color: T.textDim, fontSize: 11, fontFamily: T.fontN }}>Liquidation at 1.00</span>
        </div>
      </div>
    </Panel>
  )
}

// ─────────────────────────────────────────────────────────────────
// Shared pieces
// ─────────────────────────────────────────────────────────────────

const headerRow = {
  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
  gap: 20, flexWrap: 'wrap', marginBottom: 8,
}
const detailLink = {
  textAlign: 'center', color: T.accent, fontFamily: T.fontM,
  fontSize: T.fs.caption, textDecoration: 'none',
}
const inputLabel = {
  color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700,
  letterSpacing: '0.06em', textTransform: 'uppercase',
}
const statStrip = {
  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', marginTop: 8,
  borderRadius: T.r.md, border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.02)', overflow: 'hidden',
}

const table = { width: '100%', borderCollapse: 'collapse', minWidth: 720 }
const th = {
  textAlign: 'left', padding: '14px 20px', color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption,
  fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: `1px solid ${T.border}`,
}
const td = { padding: '16px 20px', borderBottom: `1px solid ${T.border}` }
const cellPrimary = { color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, fontWeight: 700 }
const cellSub = { marginTop: 2, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }
const marketRow = { cursor: 'pointer', transition: 'background 0.15s ease' }
const rowBtn = { padding: '7px 14px', fontSize: T.fs.caption }
// Warning-toned, not accent-toned: "Isolated" is a constraint on what you can do with the asset,
// not a feature of it, and it should read closer to a caution than to a highlight.
const isolatedPill = {
  padding: '2px 8px', borderRadius: T.r.pill, border: `1px solid ${T.warning}88`, background: `${T.warning}18`,
  color: T.warning, fontFamily: T.fontM, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
}
// Quiet rather than warning-coloured: collateral-only is a property of the listing, not a caution.
const collateralOnlyPill = {
  padding: '2px 8px', borderRadius: T.r.pill, border: `1px solid ${T.border}`,
  color: T.textDim, fontFamily: T.fontM, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
}
// The most-recently-listed market, positive-coloured: a genuine "new here" signal, not a caution.
const newPill = {
  padding: '2px 8px', borderRadius: T.r.pill, border: `1px solid ${T.positive}88`, background: `${T.positive}18`,
  color: T.positive, fontFamily: T.fontM, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
}
